<template>
  <div class="referral-page" :dir="$i18n.locale === 'ar' ? 'rtl' : 'ltr'">
    <div v-if="loading" class="referral-state" role="status"><loading-spinner /></div>
    <div v-else-if="error" class="referral-state" role="alert">
      <p>{{ $t('referrals.error') }}</p>
      <button class="btn btn-outline-primary" type="button" @click="load">{{ $t('referrals.retry') }}</button>
    </div>
    <template v-else-if="referral">
      <header class="referral-heading">
        <div class="referral-hero-copy">
        <p class="referral-eyebrow"><feather-icon icon="UserPlusIcon" size="18" /> {{ $t('buddyDesign.eyebrow') }}</p>
        <h2>{{ heroTitle.start }} <span class="referral-highlight">{{ heroTitle.end }}</span></h2>
        <p v-if="bonusRates.length" class="referral-subtitle">{{ $t('buddyDesign.subtitle', rateLabels) }}</p>
        </div>
        <div v-if="bigPackRow" class="referral-big-pack">
          <div>
            <h3>{{ $t('buddyDesign.bigPackTitle') }}</h3>
            <p>{{ $t('buddyDesign.bigPackPurchase', { diamonds: format(bigPackRow.diamonds) }) }}</p>
          </div>
          <div class="referral-pack-amount"><strong>+{{ format(bigPackRow.first_extra_beans) }}</strong><span>{{ $t('buddyDesign.extraBeans') }}</span></div>
          <div class="referral-pack-amount"><strong>{{ format(bigPackRow.beans[0]) }}</strong><span>{{ $t('buddyDesign.totalBeans') }}</span></div>
        </div>
      </header>

      <section v-if="bonusRates.length" class="referral-section">
        <h3>{{ $t('buddyDesign.ratesTitle') }}</h3>
        <div class="referral-rates">
          <article v-for="(rate, index) in bonusRates" :key="index" class="referral-rate">
            <span>{{ $t('buddyDesign.payments.' + index) }}</span>
            <strong>{{ percent(rate) }}</strong>
            <small>{{ $t('referralDetails.bonus') }}</small>
          </article>
        </div>
        <p class="referral-note">{{ $t('buddyDesign.ratesHint') }}</p>
      </section>

      <div class="referral-overview">
        <section v-if="exampleRow" class="referral-example">
          <h3>{{ $t('buddyDesign.exampleTitle', { diamonds: format(exampleRow.diamonds) }) }}</h3>
          <div v-for="(beans, index) in exampleRow.beans" :key="index" class="referral-summary-row">
            <span>{{ $t('buddyDesign.payments.' + index) }}</span>
            <strong>+{{ format(beans) }} {{ $t('Beans') }}</strong>
          </div>
          <p class="referral-note">{{ $t('buddyDesign.exampleNote') }}</p>
        </section>
        <section class="referral-stats">
          <h3>{{ $t('buddyDesign.invitesTitle') }}</h3>
          <div class="referral-summary-row"><span><feather-icon icon="ClockIcon" size="16" /> {{ $t('referrals.pending') }}</span><strong>{{ format(referral.pending_credits) }}</strong></div>
          <div class="referral-summary-row"><span><feather-icon icon="CheckCircleIcon" size="16" /> {{ $t('referrals.approved') }}</span><strong class="referral-paid">{{ format(referral.approved_credits) }}</strong></div>
        </section>
      </div>

        <div class="referral-share-heading">
          <feather-icon icon="Share2Icon" size="28" />
          <div><h3>{{ $t('buddyDesign.shareTitle') }}</h3><p>{{ $t('buddyDesign.shareSubtitle') }}</p></div>
        </div>
      <section class="referral-share">
        <label for="referral-code">{{ $t('referrals.code') }}</label>
        <div class="referral-field">
          <input id="referral-code" :value="referral.code" readonly dir="ltr" class="referral-code" @focus="$event.target.select()" />
          <button type="button" :aria-label="$t('referrals.copyCode')" :title="$t('referrals.copyCode')" :disabled="!referral.code" @click="copy(referral.code)"><feather-icon icon="CopyIcon" size="20" /></button>
        </div>
        <p class="referral-note">{{ $t('buddyDesign.codeHint') }}</p>
        <label for="referral-link">{{ $t('referrals.link') }}</label>
        <div class="referral-field">
          <input id="referral-link" :value="referral.web_url" readonly dir="ltr" @focus="$event.target.select()" />
          <button type="button" :aria-label="$t('referrals.copyLink')" :title="$t('referrals.copyLink')" :disabled="!referral.web_url" @click="copy(referral.web_url)"><feather-icon icon="CopyIcon" size="20" /></button>
        </div>
        <p class="referral-note">{{ $t('referrals.linkHelp') }}</p>
        <div class="referral-copy-status" role="status" aria-live="polite">{{ copyStatus }}</div>
      </section>

      <section class="referral-invitees referral-section">
        <h3>{{ $t('referralDetails.invitees') }}</h3>
        <div v-if="listLoading && !invitees.length" class="referral-list-state" role="status"><loading-spinner /></div>
        <div v-else-if="listError" role="alert"><p>{{ $t('referrals.error') }}</p><button type="button" class="btn btn-outline-primary" @click="loadInvitees(false)">{{ $t('referrals.retry') }}</button></div>
        <p v-else-if="!invitees.length" class="referral-note">{{ $t('referralDetails.empty') }}</p>
        <article v-for="invitee in invitees" :key="invitee.id" class="referral-invitee">
          <div class="referral-person"><b-avatar :src="invitee.avatar_url" :text="(invitee.name || '').slice(0, 1).toUpperCase()" size="36" class="referral-initial" /><div><strong>{{ invitee.name }}</strong><small>{{ $t('referralDetails.joined') }} {{ date(invitee.code_entered_at) }}</small></div></div>
          <div v-if="!invitee.payments.length" class="referral-payment">
            <span>{{ $t(expired(invitee.first_payment_deadline) ? 'referralDetails.expired' : 'referralDetails.awaiting') }}</span>
            <span v-if="!expired(invitee.first_payment_deadline)">{{ remaining(invitee.first_payment_deadline) }}</span>
            <button v-if="!expired(invitee.first_payment_deadline)" class="btn btn-primary" type="button" @click="chat(invitee)"><feather-icon icon="MessageCircleIcon" size="16" /> {{ $t('referralDetails.write') }}</button>
          </div>
          <div v-for="payment in invitee.payments" :key="payment.order" class="referral-payment">
            <span>{{ $t('buddyDesign.payments.' + (payment.order - 1)) }} <small>{{ format(payment.purchased_credits) }} {{ $t('referralDetails.diamonds') }} · {{ date(payment.purchased_at) }}</small></span>
            <span class="referral-payment-status" :class="payment.status">{{ $t('referrals.' + payment.status) }}<small v-if="payment.status === 'pending'">{{ remaining(payment.release_at) }}</small></span>
            <strong>{{ payment.status === 'reversed' ? '' : '+' }}{{ format(payment.credits) }}</strong>
          </div>
        </article>
        <button v-if="hasMore" type="button" class="btn btn-outline-primary" :disabled="listLoading" @click="loadInvitees(true)">{{ $t('referralDetails.more') }}</button>
      </section>

      <section class="referral-steps referral-section">
        <h3>{{ $t('buddyDesign.stepsTitle') }}</h3>
        <ol>
          <li v-for="(step, index) in steps" :key="index"><span class="referral-step-number" aria-hidden="true">{{ String(index + 1).padStart(2, '0') }}</span><span>{{ step }}</span></li>
        </ol>
      </section>

      <section class="referral-earnings referral-section">
        <h3>{{ $t('referralDetails.earnings') }}</h3>
        <div v-if="bonusTable.length" class="referral-table-scroll">
          <table class="referral-bonus-table">
            <thead><tr><th scope="col">{{ $t('referralDetails.diamonds') }}</th><th v-for="index in 3" :key="index" scope="col">{{ $t('buddyDesign.payments.' + (index - 1)) }}</th></tr></thead>
            <tbody><tr v-for="row in bonusTable" :key="row.diamonds" :class="{ 'referral-large-row': row.first_extra_beans }">
              <th scope="row">{{ format(row.diamonds) }}</th>
              <td v-for="(beans, index) in row.beans" :key="index">{{ format(beans) }}<small v-if="index === 0 && row.first_extra_beans">({{ format(row.first_base_beans) }} + {{ format(row.first_extra_beans) }})</small></td>
            </tr></tbody>
          </table>
        </div>
      </section>

      <section class="referral-terms referral-section">
        <h3>{{ $t('buddyDesign.rulesTitle') }}</h3>
        <p>{{ $t('buddyDesign.limits', { minimum: format(rules.min_pack_diamonds), maximum: format(rules.max_bonus_diamonds) }) }}</p>
        <p>{{ $t('buddyDesign.qualifying') }}</p>
        <p v-if="bigPackRow">{{ $t('referralBonus.bigPack', { package: format(bigPackRow.diamonds), base: format(bigPackRow.first_base_beans), extra: format(bigPackRow.first_extra_beans) }) }}</p>
        <p>{{ $t('buddyDesign.eligibility') }}</p>
        <p>{{ $t('referralDetails.legacy') }}</p>
        <p>{{ $t('buddyDesign.release', { days: rules.release_days }) }}</p>
      </section>
    </template>
  </div>
</template>

<script>
import useJwt from '@/auth/jwt/useJwt'
import { BAvatar } from 'bootstrap-vue'

export default {
  components: { BAvatar },
  data: () => ({ referral: null, loading: true, error: false, copyStatus: '', invitees: [], listLoading: false, listError: false, hasMore: false, now: Date.now(), clock: null }),
  computed: {
    heroTitle() {
      const title = this.$t('buddyDesign.title')
      const split = title.lastIndexOf(' ')
      return split < 0 ? { start: '', end: title } : { start: title.slice(0, split), end: title.slice(split + 1) }
    },
    numberLocale() { return { ua: 'uk-UA', pt: 'pt-BR' }[this.$i18n.locale] || this.$i18n.locale || 'en' },
    bonusRates() {
      // Language only changes the copy; the API's creator market selects the rates.
      const key = String(this.referral && this.referral.market || '').toUpperCase() === 'TR' ? 'rate_tr' : 'rate_global'
      const rates = this.rules[key]
      return Array.isArray(rates) && rates.length === 3 ? rates : []
    },
    rateLabels() { return { first: this.percent(this.bonusRates[0]), second: this.percent(this.bonusRates[1]), third: this.percent(this.bonusRates[2]) } },
    exampleRow() { return this.bonusTable.find(row => Number(row.diamonds) === Number(this.rules.max_bonus_diamonds)) || null },
    steps() {
      return [
        this.$t('buddyDesign.stepShare'),
        this.$t('buddyDesign.stepClaim', { hours: this.rules.code_window_hours }),
        this.$t('buddyDesign.stepFirst', { hours: this.rules.first_payment_window_hours }),
        this.$t('buddyDesign.stepRepeat', { days: this.rules.repeat_payment_window_days }),
      ]
    },
    rules() { return Object.assign({ code_window_hours: 24, first_payment_window_hours: 48, repeat_payment_window_days: 30, release_days: 14, min_pack_diamonds: 2500, max_bonus_diamonds: 20000 }, this.referral && this.referral.rules ? this.referral.rules : {}) },
    bonusTable() { return this.referral && Array.isArray(this.referral.bonus_table) ? this.referral.bonus_table : [] },
    bigPackRow() { return this.bonusTable.find(row => Number(row.first_extra_beans) > 0) || null },
  },
  created() { this.load(); this.clock = setInterval(() => { this.now = Date.now() }, 60000) },
  beforeDestroy() { clearInterval(this.clock) },
  methods: {
    format(value) { return new Intl.NumberFormat(this.numberLocale).format(Number(value) || 0) },
    percent(value) { return new Intl.NumberFormat(this.numberLocale, { style: 'percent', maximumFractionDigits: 1 }).format(Number(value) || 0) },
    async load() {
      this.loading = true
      this.error = false
      try {
        const response = await useJwt.referralMe()
        const body = response.data.body
        if (!body || !body.code || !body.web_url) throw new Error('Invalid referral response')
        this.referral = body
        await this.loadInvitees(false)
      } catch (error) { this.error = true } finally { this.loading = false }
    },
    async loadInvitees(append) {
      if (this.listLoading) return
      this.listLoading = true
      this.listError = false
      try {
        const response = await useJwt.referralInvitees(append ? this.invitees.length : 0)
        const body = response.data.body
        if (!body || !Array.isArray(body.items)) throw new Error('Invalid invitee response')
        this.invitees = append ? this.invitees.concat(body.items) : body.items
        this.hasMore = body.has_more
      } catch (error) { this.listError = true } finally { this.listLoading = false }
    },
    expired(value) { return new Date(value).getTime() <= this.now },
    remaining(value) {
      const deadline = new Date(value).getTime()
      if (!Number.isFinite(deadline)) return ''
      const hours = Math.max(0, Math.ceil((deadline - this.now) / 3600000))
      const days = Math.floor(hours / 24)
      const remainder = hours % 24
      return [days ? `${days} ${this.$t('referralDetails.days')}` : '', remainder || !days ? `${remainder} ${this.$t('referralDetails.hours')}` : ''].filter(Boolean).join(' ')
    },
    date(value) { return value ? new Date(value).toLocaleString(this.numberLocale) : '' },
    chat(invitee) { this.$router.push({ name: 'chat', params: { userId: invitee.uuid } }) },
    async copy(value) {
      this.copyStatus = ''
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          try {
            await navigator.clipboard.writeText(value)
            this.copyStatus = this.$t('referrals.copied')
            return
          } catch (error) { /* Fall back to the project's WebView-compatible copy helper. */ }
        }
        await this.$copyText(value)
        this.copyStatus = this.$t('referrals.copied')
      } catch (error) { this.copyStatus = this.$t('referrals.copyError') }
    },
  },
}
</script>

<style scoped>
.referral-page { max-width: 1120px; margin: 16px auto 32px; padding: 0 24px 28px; color: #393544; background: #f6f4fa; line-height: 1.5; text-align: start; letter-spacing: 0; }
.referral-page * { box-sizing: border-box; min-width: 0; }
.referral-page h3 { margin: 0 0 18px; color: #514465; font-size: 22px; font-weight: 700; line-height: 1.35; overflow-wrap: anywhere; }
.referral-page p { overflow-wrap: anywhere; }
.referral-heading { margin: 0 -24px; padding: 28px 32px; background: #571276; color: #fff; }
.referral-eyebrow { display: flex; align-items: center; gap: 10px; margin-bottom: 16px; color: #ffcf64; font-size: 13px; font-weight: 700; }
.referral-heading h2 { max-width: 800px; margin: 0 0 14px; color: #fff; font-size: 32px; font-weight: 800; line-height: 1.2; overflow-wrap: anywhere; }
.referral-subtitle { margin-bottom: 0; color: #f2e8f5; font-size: 17px; }
.referral-big-pack { display: grid; grid-template-columns: 1.2fr 1fr 1fr; gap: 22px; align-items: center; margin-top: 24px; padding-top: 20px; border-top: 1px solid #9a61b2; }
.referral-big-pack h3 { margin-bottom: 6px; color: #fff; font-size: 14px; }
.referral-big-pack p { margin: 0; font-size: 13px; color: #f2e8f5; }
.referral-pack-amount strong { display: block; color: #ffcf64; font-size: 30px; font-weight: 800; overflow-wrap: anywhere; }
.referral-pack-amount span { display: block; font-size: 13px; }
.referral-section { margin-top: 32px; }
.referral-rates { display: grid; grid-template-columns: repeat(3,minmax(0,1fr)); gap: 16px; }
.referral-rate { padding: 20px; border: 1px solid #e6dced; border-radius: 8px; background: #fff; }
.referral-rate > span { color: #74219a; font-size: 14px; font-weight: 600; }
.referral-rate strong { display: block; margin-top: 12px; color: #7900b5; font-size: 36px; line-height: 1.2; overflow-wrap: anywhere; }
.referral-rate small { display: block; margin-top: 4px; color: #716b7b; font-size: 13px; }
.referral-note { margin: 12px 0 0; color: #716b7b; font-size: 13px; }
.referral-overview { display: grid; grid-template-columns: repeat(2,minmax(0,1fr)); gap: 32px; margin-top: 32px; padding: 24px 0; border-block: 1px solid #ded9e6; }
.referral-stats { align-self: start; }
.referral-summary-row { display: flex; justify-content: space-between; align-items: center; gap: 16px; padding: 12px 0; border-bottom: 1px solid #e6e1eb; }
.referral-summary-row span { color: #716b7b; font-size: 14px; overflow-wrap: anywhere; }
.referral-summary-row svg { vertical-align: middle; margin-inline-end: 4px; flex-shrink: 0; }
.referral-summary-row strong { color: #4c2670; font-size: 20px; overflow-wrap: anywhere; }
.referral-summary-row .referral-paid { color: #18865e; }
.referral-share-heading { display: flex; align-items: center; gap: 16px; margin-bottom: 24px; color: #74219a; }
.referral-share-heading > svg { flex-shrink: 0; }
.referral-share-heading h3 { margin-bottom: 4px; }
.referral-share-heading p { margin: 0; color: #716b7b; }
.referral-share label { display: block; margin: 20px 0 8px; color: #514465; font-size: 16px; font-weight: 700; }
.referral-field { display: flex; height: 56px; border: 1px solid #ded9e6; border-radius: 8px; overflow: hidden; background: #fff; }
.referral-field input { flex: 1; width: 100%; min-width: 0; border: 0; background: transparent; padding: 12px 16px; font-size: 14px; color: #393544; }
.referral-field .referral-code { font-size: 23px; font-weight: 700; }
.referral-field button { width: 56px; flex: 0 0 56px; border: 0; background: #f0e4f8; color: #7900b5; cursor: pointer; }
.referral-field button:hover { background: #e9d6f3; }
.referral-field button:disabled { opacity: .5; cursor: default; }
.referral-field button:focus-visible, .referral-field input:focus-visible { outline: 2px solid #7900b5; outline-offset: -3px; }
.referral-copy-status { min-height: 24px; margin-top: 8px; font-size: 13px; color: #571276; }
.referral-state { min-height: 220px; display: flex; flex-direction: column; align-items: center; justify-content: center; }
.referral-invitee { border-bottom: 1px solid #ded9e6; padding: 18px 0; }
.referral-person { display: flex; align-items: center; gap: 12px; margin-bottom: 12px; }
.referral-person > div { overflow-wrap: anywhere; }
.referral-initial { flex: 0 0 36px; background: #f0e4f8; color: #7900b5; }
.referral-invitee small { display: block; font-size: 12px; color: #716b7b; }
.referral-payment { display: flex; align-items: center; flex-wrap: wrap; gap: 12px; padding: 8px 0; overflow-wrap: anywhere; }
.referral-payment > span:first-child { flex: 1; min-width: 140px; }
.referral-payment strong { color: #4c2670; }
.referral-payment-status.approved { color: #18865e; }
.referral-payment-status.reversed { color: #b4363a; }
.referral-page .btn { white-space: normal; }
.referral-steps ol { list-style: none; padding: 0; margin: 0; }
.referral-steps li { display: flex; align-items: flex-start; gap: 16px; margin: 16px 0; }
.referral-step-number { flex: 0 0 36px; height: 36px; display: grid; place-items: center; border-radius: 6px; background: #eee2f5; color: #7900b5; font-weight: 700; }
.referral-steps li > span:last-child { padding-top: 6px; overflow-wrap: anywhere; }
.referral-table-scroll { overflow-x: auto; }
.referral-bonus-table { width: 100%; border-collapse: collapse; table-layout: fixed; font-size: 14px; }
.referral-bonus-table th, .referral-bonus-table td { padding: 14px 8px; border-bottom: 1px solid #ded9e6; text-align: end; overflow-wrap: anywhere; }
.referral-bonus-table th:first-child { text-align: start; }
.referral-bonus-table thead th { color: #716b7b; font-weight: 600; }
.referral-bonus-table small { display: block; font-size: 11px; }
.referral-large-row { background: #fff1cf; color: #553715; }
.referral-terms { border-top: 1px solid #ded9e6; padding-top: 24px; }
.referral-terms p { margin: 12px 0; color: #716b7b; font-size: 13px; }
@media (max-width: 760px) {
  .referral-page { padding: 0 16px 24px; }
  .referral-heading { margin-inline: -16px; padding: 24px 20px; }
  .referral-heading h2 { font-size: 26px; }
  .referral-subtitle { font-size: 15px; }
  .referral-big-pack { grid-template-columns: repeat(2,minmax(0,1fr)); gap: 16px; }
  .referral-big-pack > div:first-child { grid-column: 1 / -1; }
  .referral-pack-amount strong { font-size: 28px; }
  .referral-overview { grid-template-columns: 1fr; gap: 28px; }
  .referral-page h3 { font-size: 20px; }
  .referral-big-pack h3 { font-size: 14px; }
  .referral-rates { gap: 8px; }
  .referral-rate { padding: 14px 10px; }
  .referral-rate strong { font-size: 27px; }
  .referral-rate > span, .referral-rate small { font-size: 12px; overflow-wrap: anywhere; }
  .referral-bonus-table { font-size: 12px; }
  .referral-bonus-table th, .referral-bonus-table td { padding: 12px 4px; }
}
.referral-page { max-width: 1270px; padding: 32px 20px 56px; font-size: 16px; }
.referral-heading { min-height: 292px; margin: 0; padding: 30px 40px; border-radius: 28px; background: linear-gradient(112deg,#571276 0%,#611383 58%,#7a2199 100%); display: grid; grid-template-columns: minmax(0,1.2fr) minmax(0,.8fr); gap: 28px; align-items: center; box-shadow: 0 14px 32px rgba(77,18,102,.12); }
.referral-eyebrow { display: inline-flex; padding: 8px 17px; margin: 0; border-radius: 14px; background: #ffbf36; color: #4f2b08; font-size: 14px; font-weight: 800; }
.referral-heading h2 { margin: 18px 0 12px; font-size: 43px; line-height: 1.18; }
.referral-highlight { display: inline-block; padding: 0 .22em .04em; border-radius: 10px; background: #ffbf36; color: #51246d; }
.referral-subtitle { font-size: 18px; }
.referral-big-pack { display: block; margin: 0; padding: 20px 26px; border: 1px solid rgba(255,255,255,.13); border-radius: 22px; background: rgba(132,37,168,.82); }
.referral-pack-amount { margin-top: 4px; }
.referral-pack-amount strong { display: inline; color: #ffbf36; font-size: 31px; line-height: 1.2; }
.referral-pack-amount span { display: inline; margin-inline-start: 6px; font-size: 14px; font-weight: 700; }
.referral-pack-amount:last-child { margin-top: 10px; }
.referral-pack-amount:last-child strong { font-size: 40px; line-height: 1.1; }
.referral-section { margin-top: 36px; }
.referral-page h3 { font-size: 27px; }
.referral-big-pack h3 { font-size: 14px; }
.referral-rates { gap: 20px; }
.referral-rate { min-height: 145px; padding: 24px; border: 0; border-radius: 22px; box-shadow: 0 8px 24px rgba(47,31,61,.035); }
.referral-rate > span { display: table; padding: 7px 15px; border-radius: 999px; background: #f4e8fb; font-weight: 800; }
.referral-rate strong { display: inline-block; margin-top: 9px; font-size: 42px; line-height: 1.1; }
.referral-rate small { display: inline; margin-inline-start: 7px; font-size: 16px; }
.referral-note { font-size: 14px; }
.referral-overview { gap: 24px; margin-top: 36px; padding: 0; border: 0; }
.referral-example,.referral-stats { min-height: 230px; padding: 24px; background: #fff; border-radius: 22px; box-shadow: 0 8px 24px rgba(47,31,61,.035); }
.referral-example h3,.referral-stats h3 { font-size: 20px; }
.referral-example .referral-summary-row { border: 0; padding: 10px 0; }
.referral-summary-row strong { font-size: 18px; }
.referral-stats .referral-summary-row strong { font-size: 22px; }
.referral-share-heading { margin: 30px 0 18px; padding: 16px 22px; border-radius: 20px; background: #f0e4f8; }
.referral-share-heading > svg { box-sizing: content-box; padding: 10px; border-radius: 14px; background: #6d1595; color: #fff; }
.referral-share-heading h3 { font-size: 20px; margin: 0; }
.referral-share-heading p { color: #6d1595; }
.referral-share,.referral-steps,.referral-terms { padding: 28px 32px; border: 0; border-radius: 22px; background: #fff; box-shadow: 0 8px 24px rgba(47,31,61,.035); }
.referral-share label { margin: 0 0 9px; font-size: 19px; }
.referral-share .referral-note { margin: 8px 0 20px; }
.referral-field { min-height: 60px; height: auto; padding: 10px 16px 10px 20px; gap: 12px; border-radius: 12px; background: #faf9fc; }
.referral-field input,.referral-field .referral-code { padding: 0; font-size: 16px; font-weight: 600; }
.referral-field button { width: 40px; height: 40px; flex-basis: 40px; border-radius: 9px; }
.referral-steps h3,.referral-terms h3 { font-size: 20px; }
.referral-step-number { flex-basis: 44px; height: 38px; border-radius: 12px; }
.referral-terms p { font-size: 14px; }
@media (max-width: 760px) {
  .referral-page { padding: 16px 12px 36px; }
  .referral-heading { grid-template-columns: minmax(0,1fr); padding: 24px; gap: 20px; }
  .referral-heading h2 { font-size: 30px; }
  .referral-big-pack { padding: 20px 26px; }
  .referral-rates { grid-template-columns: minmax(0,1fr); gap: 12px; }
  .referral-rate { min-height: auto; padding: 22px; }
  .referral-rate > span { display: table; font-size: 14px; }
  .referral-rate strong { font-size: 42px; }
  .referral-rate small { font-size: 16px; }
  .referral-overview { grid-template-columns: minmax(0,1fr); gap: 16px; }
  .referral-example,.referral-stats,.referral-share,.referral-steps,.referral-terms { padding: 22px; }
  .referral-section { margin-top: 28px; }
  .referral-page h3 { font-size: 23px; }
  .referral-big-pack h3 { font-size: 14px; }
  .referral-example h3,.referral-stats h3,.referral-steps h3,.referral-terms h3 { font-size: 20px; }
  .referral-share-heading { margin-top: 22px; padding: 16px; gap: 12px; }
  .referral-share-heading h3 { font-size: 17px; }
  .referral-field { padding: 10px 12px; gap: 8px; }
  .referral-field input { font-size: 13px; }
  .referral-pack-amount strong { font-size: 31px; }
}
</style>
